/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {PERMISSIONS} from '@agent-assist/common/lib/admin/rbac';
import AutoModeIcon from '@mui/icons-material/AutoMode';
import CodeIcon from '@mui/icons-material/Code';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import InsightsIcon from '@mui/icons-material/Insights';
import MenuBookIcon from '@mui/icons-material/MenuBook';

import {FeatureFlags} from './flags';
import {ROUTES} from './routes';
import {getSiteURL} from './services/utils';
import type {PrimaryColor} from './styles/types';
import type {NavItemConfig} from './types/nav';

export const muiLicenseKey =
  'd4d0215f69c4f6e93830a6d77fbd2da4Tz05ODA3NCxFPTE3NTc4MjYyNTUwMDAsUz1wcmVtaXVtLExNPXN1YnNjcmlwdGlvbixQVj1pbml0aWFsLEtWPTI=';

/**
 * GENERAL CONFIG
 */
interface Config {
  site: {
    name: string;
    description: string;
    themeColor: string;
    primaryColor: PrimaryColor;
    url: string;
  };
}
export const config = {
  site: {
    name: 'Trusst AI',
    description: '',
    themeColor: '#090a0b',
    primaryColor: 'trusst',
    url: getSiteURL(),
  },
} satisfies Config;

/**
 * LAYOUT CONFIG
 */
export type LayoutConfig = {
  navMainItems: NavItemConfig[];
  navSecondaryItems: NavItemConfig[];
};
export const layoutConfig = {
  navMainItems: [
    {
      key: 'dashboards',
      items: [
        {
          key: 'insights',
          title: 'Insights',
          href: ROUTES.ANALYTICS,
          icon: InsightsIcon,
          matcher: {type: 'startsWith', href: ROUTES.ANALYTICS},
          permissionsRequired: [PERMISSIONS.INSIGHTS_READ],
          featureFlag: FeatureFlags.functionInsightsAnalytics,
        },
        {
          key: 'contacts',
          title: 'Contacts',
          href: ROUTES.CONTACT_IMPORTS_LIST,
          icon: FingerprintIcon,
          matcher: {type: 'startsWith', href: ROUTES.CONTACT_IMPORTS_LIST},
          permissionsRequired: [PERMISSIONS.CONTACTS_READ],
          featureFlag: FeatureFlags.functionInsightsAnalytics,
        },
        {
          key: 'criteria',
          title: 'Criteria',
          href: ROUTES.PROMPTS,
          icon: AutoModeIcon,
          matcher: {type: 'startsWith', href: ROUTES.PROMPTS},
          permissionsRequired: [PERMISSIONS.PROMPTS_READ],
          featureFlag: FeatureFlags.functionInsightsAnalytics,
        },
        {
          key: 'agents',
          title: 'Agents',
          href: ROUTES.AGENTS_LIST,
          icon: HeadsetMicIcon,
          matcher: {type: 'startsWith', href: ROUTES.AGENTS_LIST},
          featureFlag: FeatureFlags.functionTrusstedAgent,
          permissionsRequired: [PERMISSIONS.TRUSSTED_AGENT_READ],
        },
      ],
    },
  ],
  navSecondaryItems: [
    {
      key: 'extras',
      items: [
        {
          key: 'dev-tools',
          title: 'Dev Tools',
          href: ROUTES.DEV,
          icon: CodeIcon,
          matcher: {type: 'startsWith', href: ROUTES.DEV},
          featureFlag: FeatureFlags.devToolsEnabled,
        },
        {
          key: 'user-guide',
          title: 'User Guide',
          external: true,
          href: 'https://docs.trusst.ai/product-guides/user-guide',
          icon: MenuBookIcon,
        },
      ],
    },
  ],
} satisfies LayoutConfig;
